import React from 'react';
import '../assets/FleetAnalytics.css';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import {
    renderMetricsCard,
    renderExtremesCard,
    renderAnomaliesCard,
    renderTrendsCard,
    renderPatternsCard,
    renderBaselineCard,
    renderRecoveryHierarchyCard,
    renderCacheInfo,
    AIErrorCard,
} from '../aiInsights/aiCards';

const DURATIONS = [
    { key: '1d',  label: 'D' },
    { key: '7d',  label: 'W' },
    { key: '30d', label: 'M', disabled: true },
];

const durationToAIDays = (key) => ({ '1d': 1, '7d': 7, '30d': 30 }[key] || 7);

function SectionHeader({ icon, tone = 'blue', title, subtitle, first = false }) {
    return (
        <div className="sec-hdr" style={first ? { marginTop: 0 } : undefined}>
            <div className={`sec-icon ${tone}`}>{icon}</div>
            <h2>{title}</h2>
            {subtitle && <span className="sec-sub">{subtitle}</span>}
            <div className="line" />
        </div>
    );
}

// ── MBB Telemetry AI Insights sub-tab ──
// Self-contained AI Insights view lifted out of FleetAnalyticsDashboard. Owns
// its own Duration + View (Aggregate / Device Tag) filter state and the AI
// insight fetch pipeline (APIGEE token + 4 insight types).
function MbbTelemetryAIInsights({ userInfo, role }) { // eslint-disable-line no-unused-vars

    // ── View type + tag ──
    const [viewType, setViewType] = React.useState('aggregate'); // 'aggregate' | 'device-tag'
    const [selectedTag, setSelectedTag] = React.useState(null);
    const [availableTags, setAvailableTags] = React.useState([]);
    const [tagsLoading, setTagsLoading] = React.useState(false);

    // ── Duration ──
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('1d');

    // ── AI Insights state ──
    const [aiActiveTab, setAiActiveTab] = React.useState('data-stall');
    const [dataStallData, setDataStallData] = React.useState(null);
    const [peakRateULData, setPeakRateULData] = React.useState(null);
    const [peakRateDLData, setPeakRateDLData] = React.useState(null);
    const [thermalData, setThermalData] = React.useState(null);
    const [dataStallLoading, setDataStallLoading] = React.useState(false);
    const [peakRateULLoading, setPeakRateULLoading] = React.useState(false);
    const [peakRateDLLoading, setPeakRateDLLoading] = React.useState(false);
    const [thermalLoading, setThermalLoading] = React.useState(false);
    const [dataStallError, setDataStallError] = React.useState(null);
    const [peakRateULError, setPeakRateULError] = React.useState(null);
    const [peakRateDLError, setPeakRateDLError] = React.useState(null);
    const [thermalError, setThermalError] = React.useState(null);
    const [cacheMetadata, setCacheMetadata] = React.useState({ dataStall: null, peakRateUL: null, peakRateDL: null, thermal: null });
    const [deviceCounts, setDeviceCounts] = React.useState({ dataStall: null, peakRateUL: null, peakRateDL: null, thermal: null });
    const [retryAttempts, setRetryAttempts] = React.useState({ dataStall: 0, peakRateUL: 0, peakRateDL: 0, thermal: 0 });
    const apigeeTokenRef = React.useRef(null);

    const MAX_RETRY = 3;

    // ── AI fetch helpers ──
    const buildInsightUrl = React.useCallback((apiVersion, insightType, testType, vt, tag) => {
        const base = process.env.REACT_APP_WBC_AI_INSIGHTS_URL;
        const suffix = testType ? `&test_type=${testType}` : '';
        if (vt === 'device-tag' && tag) {
            return `${base}/api/${apiVersion}/insights/device-tag/${tag}/${insightType}?days=${durationToAIDays(selectedTimeRange)}${suffix}`;
        }
        return `${base}/api/${apiVersion}/insights/aggregate/${insightType}?days=${durationToAIDays(selectedTimeRange)}${suffix}`;
    }, [selectedTimeRange]);

    const fetchInsight = React.useCallback(async (url, insightType, setData, setError, setLoading, metaKey, retryKey, testType, vt, tag, token) => {
        const attempt = retryAttempts[retryKey];
        try {
            setLoading(true);
            setError(null);
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const response = await fetch(url, { headers });
            switch (response.status) {
                case 200: {
                    const d = await response.json();
                    setData(d.insights);
                    if (d.device_count != null) setDeviceCounts(prev => ({ ...prev, [retryKey]: d.device_count }));
                    if (d.cache_metadata) setCacheMetadata(prev => ({ ...prev, [metaKey]: d.cache_metadata }));
                    setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
                    break;
                }
                case 202: {
                    const d = await response.json();
                    if (attempt < MAX_RETRY) {
                        const after = d.retry_after || 30;
                        setError({ type: 'warming', message: `${d.message} (Attempt ${attempt + 1}/${MAX_RETRY})`, retryAfter: after });
                        setRetryAttempts(prev => ({ ...prev, [retryKey]: attempt + 1 }));
                        setTimeout(() => {
                            const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
                            const retryUrl = buildInsightUrl(ver, insightType, testType, vt, tag);
                            fetchInsight(retryUrl, insightType, setData, setError, setLoading, metaKey, retryKey, testType, vt, tag, token);
                        }, after * 1000);
                    } else {
                        setError({ type: 'timeout', message: 'Insights generation timed out. Please try again later.' });
                        setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
                    }
                    break;
                }
                case 404: {
                    const d = await response.json();
                    setError({ type: 'not_found', message: d.message || 'Resource not found', details: d.details });
                    setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
                    break;
                }
                case 500: {
                    const d = await response.json();
                    setError({ type: 'failed', message: d.message || 'Failed to generate insights', warning: d.warning, retryAfter: d.retry_after || 300 });
                    setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
                    break;
                }
                default:
                    setError({ type: 'unexpected', message: `Unexpected error (HTTP ${response.status}).` });
                    setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
            }
        } catch (err) {
            setError({ type: 'network', message: 'Network error. Please check your connection.' });
            setRetryAttempts(prev => ({ ...prev, [retryKey]: 0 }));
        } finally {
            setLoading(false);
        }
    }, [buildInsightUrl, retryAttempts]);

    const fetchAllAI = React.useCallback(async (vt, tag) => {
        const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2';
        // Reset AI state
        setDataStallData(null); setPeakRateULData(null); setPeakRateDLData(null);
        setThermalData(null);
        setDataStallError(null); setPeakRateULError(null); setPeakRateDLError(null);
        setThermalError(null);
        setDeviceCounts({ dataStall: null, peakRateUL: null, peakRateDL: null, thermal: null });
        setAiActiveTab('data-stall');

        // Fetch APIGEE bearer token first (same as AIInsights.js)
        let token = null;
        try {
            const tokenUrl = (process.env.REACT_APP_WBC_API_BASE_URL || '') + '/api/v1/apigee/token';
            const tokenRes = await fetch(tokenUrl);
            if (tokenRes.ok) {
                const tokenData = await tokenRes.json();
                token = tokenData.access_token || null;
            }
        } catch (e) {
            console.warn('Failed to fetch APIGEE token for Analytics AI Insights:', e.message);
        }
        apigeeTokenRef.current = token;

        fetchInsight(buildInsightUrl(ver, 'data-stall', null, vt, tag), 'data-stall', setDataStallData, setDataStallError, setDataStallLoading, 'dataStall', 'dataStall', null, vt, tag, token);
        fetchInsight(buildInsightUrl(ver, 'peak-rate', 'UPLINK', vt, tag), 'peak-rate-ul', setPeakRateULData, setPeakRateULError, setPeakRateULLoading, 'peakRateUL', 'peakRateUL', 'UPLINK', vt, tag, token);
        fetchInsight(buildInsightUrl(ver, 'peak-rate', 'DOWNLINK', vt, tag), 'peak-rate-dl', setPeakRateDLData, setPeakRateDLError, setPeakRateDLLoading, 'peakRateDL', 'peakRateDL', 'DOWNLINK', vt, tag, token);
        fetchInsight(buildInsightUrl(ver, 'thermal', null, vt, tag), 'thermal', setThermalData, setThermalError, setThermalLoading, 'thermal', 'thermal', null, vt, tag, token);
    }, [buildInsightUrl, fetchInsight]);

    // ── Load available tags when switching to device-tag view ──
    React.useEffect(() => {
        if (viewType !== 'device-tag' || availableTags.length > 0) return;
        const apiBase = (process.env.REACT_APP_WBC_API_BASE_URL || '').replace(/\/$/, '');
        const qpath = (process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1');
        setTagsLoading(true);
        fetch(`${apiBase}${qpath.startsWith('/') ? qpath : '/' + qpath}/aggregates/tags`)
            .then(r => r.json())
            .then(d => setAvailableTags(d.tags || []))
            .catch(() => setAvailableTags([]))
            .finally(() => setTagsLoading(false));
    }, [viewType, availableTags.length]);

    // ── Main effect: re-fetch AI insights on duration or viewType/tag change ──
    React.useEffect(() => {
        if (viewType === 'aggregate') {
            fetchAllAI('aggregate', null);
        } else if (selectedTag) {
            fetchAllAI('device-tag', selectedTag);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimeRange, viewType, selectedTag]);

    const handleTimeRangeChange = (key) => {
        if (DURATIONS.find(d => d.key === key)?.disabled) return;
        setSelectedTimeRange(key);
    };

    const handleViewTypeChange = (vt) => {
        if (vt === viewType) return;
        setViewType(vt);
        // Reset AI data so stale cards don't flash
        setDataStallData(null); setPeakRateULData(null); setPeakRateDLData(null);
        setThermalData(null);
    };

    const handleTagChange = (tag) => {
        setSelectedTag(tag || null);
    };

    // ── AI Insights sub-tab navigator ──
    const aiTabs = [
        { key: 'data-stall',   label: '⏸️ Data Stall',    data: dataStallData,   loading: dataStallLoading },
        { key: 'peak-rate-ul', label: '📶 Throughput UL', data: peakRateULData,  loading: peakRateULLoading },
        { key: 'peak-rate-dl', label: '📶 Throughput DL', data: peakRateDLData,  loading: peakRateDLLoading },
        { key: 'thermal',      label: '🌡️ Thermal',       data: thermalData,     loading: thermalLoading },
    ];

    // ── Control bar ──
    const controls = (
        <div className="page-intro">
            <div className="duration-bar">
                <span className="duration-label">Duration:</span>
                {DURATIONS.map(d => (
                    <span
                        key={d.key}
                        className={`filter-chip${selectedTimeRange === d.key ? ' active' : ''}${d.disabled ? ' disabled' : ''}`}
                        onClick={() => handleTimeRangeChange(d.key)}
                        title={d.disabled ? 'Not available for AI Insights' : undefined}
                    >
                        {d.label}
                    </span>
                ))}
                <span className="duration-divider" />
                <span className="duration-label">View:</span>
                <span
                    className={`filter-chip${viewType === 'aggregate' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('aggregate')}
                >
                    Aggregate
                </span>
                <span
                    className={`filter-chip${viewType === 'device-tag' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('device-tag')}
                >
                    Device Tag
                </span>
            </div>
        </div>
    );

    // ── AI Insights section (shared between both views) ──
    const renderAIInsights = (vt) => (
        <>
            <SectionHeader first icon="🤖" tone="purple" title="AI Insights" />
            <div className="ai-tabs">
                {aiTabs.map(t => (
                    <button
                        key={t.key}
                        className={`ai-tab${aiActiveTab === t.key ? ' active' : ''}`}
                        onClick={() => setAiActiveTab(t.key)}
                    >
                        {t.label} {t.loading ? '⏳' : ''}
                    </button>
                ))}
            </div>

            {/* Data Stall */}
            {aiActiveTab === 'data-stall' && (
                <div className="ai-dashboard active">
                    {renderCacheInfo(cacheMetadata.dataStall, deviceCounts.dataStall, vt)}
                    {dataStallError
                        ? <AIErrorCard error={dataStallError} onRetry={() => { const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2'; fetchInsight(buildInsightUrl(ver, 'data-stall', null, vt, selectedTag), 'data-stall', setDataStallData, setDataStallError, setDataStallLoading, 'dataStall', 'dataStall', null, vt, selectedTag, apigeeTokenRef.current); }} />
                        : dataStallLoading
                            ? <div className="card fa-card-body"><LoadingSkeleton height="200px" /></div>
                            : dataStallData
                                ? <div className="ai-metrics-grid">
                                    {renderMetricsCard(dataStallData.metrics_card, '⏸️', 'Data Stall Metrics', 'warning-bg')}
                                    {renderExtremesCard(dataStallData.extremes_card)}
                                    {renderAnomaliesCard(dataStallData.anomalies_card)}
                                    {renderTrendsCard(dataStallData.trends_card)}
                                    {renderPatternsCard(dataStallData.patterns_card)}
                                    {renderRecoveryHierarchyCard()}
                                  </div>
                                : null}
                </div>
            )}

            {/* Throughput UL */}
            {aiActiveTab === 'peak-rate-ul' && (
                <div className="ai-dashboard active">
                    {renderCacheInfo(cacheMetadata.peakRateUL, deviceCounts.peakRateUL, vt)}
                    {peakRateULError
                        ? <AIErrorCard error={peakRateULError} onRetry={() => { const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2'; fetchInsight(buildInsightUrl(ver, 'peak-rate', 'UPLINK', vt, selectedTag), 'peak-rate-ul', setPeakRateULData, setPeakRateULError, setPeakRateULLoading, 'peakRateUL', 'peakRateUL', 'UPLINK', vt, selectedTag, apigeeTokenRef.current); }} />
                        : peakRateULLoading
                            ? <div className="card fa-card-body"><LoadingSkeleton height="200px" /></div>
                            : peakRateULData
                                ? <div className="ai-metrics-grid">
                                    {renderMetricsCard(peakRateULData.metrics_card, '📶', 'Uplink Rate Metrics', 'primary-bg')}
                                    {renderExtremesCard(peakRateULData.extremes_card)}
                                    {renderAnomaliesCard(peakRateULData.anomalies_card)}
                                    {renderTrendsCard(peakRateULData.trends_card)}
                                    {renderPatternsCard(peakRateULData.patterns_card)}
                                    {renderBaselineCard(peakRateULData.baseline_card)}
                                  </div>
                                : null}
                </div>
            )}

            {/* Throughput DL */}
            {aiActiveTab === 'peak-rate-dl' && (
                <div className="ai-dashboard active">
                    {renderCacheInfo(cacheMetadata.peakRateDL, deviceCounts.peakRateDL, vt)}
                    {peakRateDLError
                        ? <AIErrorCard error={peakRateDLError} onRetry={() => { const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2'; fetchInsight(buildInsightUrl(ver, 'peak-rate', 'DOWNLINK', vt, selectedTag), 'peak-rate-dl', setPeakRateDLData, setPeakRateDLError, setPeakRateDLLoading, 'peakRateDL', 'peakRateDL', 'DOWNLINK', vt, selectedTag, apigeeTokenRef.current); }} />
                        : peakRateDLLoading
                            ? <div className="card fa-card-body"><LoadingSkeleton height="200px" /></div>
                            : peakRateDLData
                                ? <div className="ai-metrics-grid">
                                    {renderMetricsCard(peakRateDLData.metrics_card, '📶', 'Downlink Rate Metrics', 'primary-bg')}
                                    {renderExtremesCard(peakRateDLData.extremes_card)}
                                    {renderAnomaliesCard(peakRateDLData.anomalies_card)}
                                    {renderTrendsCard(peakRateDLData.trends_card)}
                                    {renderPatternsCard(peakRateDLData.patterns_card)}
                                    {renderBaselineCard(peakRateDLData.baseline_card)}
                                  </div>
                                : null}
                </div>
            )}

            {/* Thermal */}
            {aiActiveTab === 'thermal' && (
                <div className="ai-dashboard active">
                    {renderCacheInfo(cacheMetadata.thermal, deviceCounts.thermal, vt)}
                    {thermalError
                        ? <AIErrorCard error={thermalError} onRetry={() => { const ver = process.env.REACT_APP_WBC_AI_INSIGHTS_API_VERSION || 'v2'; fetchInsight(buildInsightUrl(ver, 'thermal', null, vt, selectedTag), 'thermal', setThermalData, setThermalError, setThermalLoading, 'thermal', 'thermal', null, vt, selectedTag, apigeeTokenRef.current); }} />
                        : thermalLoading
                            ? <div className="card fa-card-body"><LoadingSkeleton height="200px" /></div>
                            : thermalData
                                ? <div className="ai-metrics-grid">
                                    {renderMetricsCard(thermalData.metrics_card, '🌡️', 'Temperature Metrics', 'danger-bg')}
                                    {renderExtremesCard(thermalData.extremes_card)}
                                    {renderAnomaliesCard(thermalData.anomalies_card)}
                                    {renderTrendsCard(thermalData.trends_card)}
                                    {renderPatternsCard(thermalData.patterns_card)}
                                    {renderBaselineCard(thermalData.baseline_card)}
                                  </div>
                                : null}
                </div>
            )}
        </>
    );

    return (
        <div className="fho-dashboard">
            {controls}

            {viewType === 'device-tag' ? (
                <>
                    {/* Tag selector */}
                    <div className="tag-selector-row">
                        <span className="duration-label" style={{ marginRight: 8 }}>Device Group:</span>
                        <select
                            className="tag-select"
                            value={selectedTag || ''}
                            onChange={e => handleTagChange(e.target.value)}
                            disabled={tagsLoading}
                        >
                            <option value="">{tagsLoading ? 'Loading groups…' : availableTags.length === 0 ? 'No device groups found' : 'Select a device group…'}</option>
                            {availableTags.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>

                    {!selectedTag ? (
                        <div className="tag-empty-state">
                            <div className="tag-empty-icon">🏷️</div>
                            <div className="tag-empty-text">Select a device group to load insights</div>
                        </div>
                    ) : (
                        renderAIInsights('device-tag')
                    )}
                </>
            ) : (
                renderAIInsights('aggregate')
            )}
        </div>
    );
}

export default MbbTelemetryAIInsights;
